import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { categories, mainSiteUrl } from "@/mocks/categories";

const languages = [
  { code: "en", label: "English" },
  { code: "pl", label: "Polski" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const goHome = () => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = [
    { key: "nav.home", action: goHome },
    { key: "nav.categories", action: () => scrollTo("categories") },
    { key: "nav.articles", action: () => scrollTo("articles") },
    { key: "nav.faq", action: () => scrollTo("faq") },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background-50 border-b border-background-200">
      <div className="mx-auto max-w-7xl px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        <button onClick={goHome} className="flex items-center gap-2 cursor-pointer shrink-0">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-links-line text-lg" />
          </span>
          <span className="font-heading font-bold text-xl text-foreground-950 whitespace-nowrap">USFansLink</span>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={item.action}
              className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t(item.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-bag-3-line text-base" />
            {t("nav.shopGuide")}
          </a>

          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-global-line text-base" />
              <span className="hidden sm:inline">{languages.find((l) => l.code === i18n.language)?.label ?? "English"}</span>
              <span className="sm:hidden uppercase">{i18n.language}</span>
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-1 w-40 rounded-lg bg-background-50 border border-background-200 shadow-sm py-1 z-50">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => changeLanguage(l.code)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap ${i18n.language === l.code ? "font-semibold text-primary-600" : "text-foreground-700"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-9 h-9 rounded-md flex items-center justify-center text-foreground-800 hover:bg-background-100 cursor-pointer"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[75vh] overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={item.action}
              className="block w-full text-left px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              {t(item.key)}
            </button>
          ))}

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer"
          >
            <i className="ri-shopping-bag-3-line text-base" />
            {t("nav.shopGuide")}
          </a>

          <div className="mt-4 pt-4 border-t border-background-200">
            <p className="px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">{t("footer.categories")}</p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href={c.link}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <span className="text-primary-500"><i className="ri-price-tag-3-line" /></span>
                  <span className="truncate">{t(c.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}